import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button, buttonStyles } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { CharacterForm } from "@/features/characters/components/CharacterForm";
import { CharacterSummary } from "@/features/characters/components/CharacterSummary";
import { useCharacters } from "@/features/characters/hooks/useCharacters";
import type { CharacterInput } from "@/features/characters/types";

export function CharacterSheetPage() {
  const { characterId = "" } = useParams();
  const navigate = useNavigate();
  const { items, loading, deleteCharacter, updateCharacter, engine } = useCharacters();
  const character = items.find((item) => item.id === characterId);

  async function handleUpdate(value: CharacterInput) {
    await updateCharacter(characterId, value);
  }

  async function handleDelete() {
    const confirmed = window.confirm(
      "Deseja realmente excluir esta ficha local? Esta ação não pode ser desfeita.",
    );

    if (!confirmed) {
      return;
    }

    await deleteCharacter(characterId);
    navigate("/dashboard");
  }

  if (!loading && !character) {
    return (
      <EmptyState
        title="Ficha não encontrada"
        description="Esse personagem pode ter sido removido, ou ainda não existe no armazenamento selecionado. Volte ao dashboard para conferir a coleção."
        action={
          <Link to="/dashboard" className={buttonStyles()}>
            Voltar ao dashboard
          </Link>
        }
      />
    );
  }

  if (!character) {
    return (
      <div className="rounded-3xl border border-gold/15 bg-black/15 p-8 text-sm text-mist">
        Carregando a ficha...
      </div>
    );
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Ficha"
        title={character.name}
        description="Visualize o resumo do personagem e edite os campos da ficha sem sair da página. Todas as alterações ficam armazenadas localmente."
        actions={
          <div className="flex flex-wrap gap-3">
            <Badge tone={engine === "indexeddb" ? "moss" : "wine"}>
              {engine === "indexeddb" ? "IndexedDB" : "localStorage"}
            </Badge>
            <Button variant="danger" onClick={handleDelete}>
              Excluir ficha
            </Button>
          </div>
        }
      />

      <CharacterSummary character={character} />

      <CharacterForm
        initialValue={{
          name: character.name,
          epithet: character.epithet,
          ancestry: character.ancestry,
          className: character.className,
          origin: character.origin,
          level: character.level,
          summary: character.summary,
          goals: character.goals,
          equipment: character.equipment,
          notes: character.notes,
          attributes: character.attributes,
        }}
        submitLabel="Salvar alterações"
        onSubmit={handleUpdate}
      />
    </div>
  );
}
